import React from 'react'
import { useDrop } from 'react-dnd'

import { pxToRem } from '@/shared/styles/themes'
import { Grid, styled, Typography } from '@mui/material'

import { CustomTooltip } from '../CustomTooltip/CustomTooltip'
import { useGanttContext } from './GanttContext'
import { getPositionFromTime } from './libs/getPositionFromTime'
import { GanttChartDragItem, GanttChartDragType } from './models/GanttChartDragType'
import { GanttRow, TimelineStop, TravelLeg } from './models/GanttRow'
import { StyledTooltipPaper } from './StyledTooltip'

export type GanttLegProps = {
  color: string
  leg: TravelLeg
  row: GanttRow
  startHour: number
  baseWidth: number
  onStopDrop: (sourceStop: TimelineStop, targetStop: TimelineStop) => void
}

export const GanttLeg: React.FC<GanttLegProps> = ({ color, leg, row, startHour, baseWidth, onStopDrop }) => {
  const { zoom } = useGanttContext()

  // Convert duration (minutes) to hours and calculate width
  const getDurationWidth = (duration: number): number => {
    return (duration / 60) * (baseWidth * zoom)
  }

  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: leg.isIdleLeg ? [] : GanttChartDragType.STOP,
    drop: (item: GanttChartDragItem) => {
      if (!leg.isDroppable || !onStopDrop) return

      onStopDrop(item.stop, row.timeline.stops.find(stop => stop.id === leg.prevStopId)!)
    },
    collect: (monitor) => ({
      isOver: leg.isDroppable && monitor.isOver(),
    }),
  }), [leg, row])

  const legDefaultColor = color
  const legLeftPosition = getPositionFromTime({
    time: leg.startTime,
    startHour,
    zoom,
    baseWidth,
  })
  const legWidth = getDurationWidth(leg.duration)

  const tooltipContent = (
    <StyledTooltipPaper>
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {leg.isIdleLeg ? 'Idle Time' : 'Transit Time'}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {leg.duration} minutes
          </Typography>
        </Grid>
      </Grid>
      {leg.transitDistance && <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Transit Distance
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {leg.transitDistance} kms
          </Typography>
        </Grid>
      </Grid>}

      {leg.isLoadEnd && <Grid container justifyContent={'flex-end'} alignItems={'center'}>
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {leg.loadNumber} ends
          </Typography>
        </Grid>
      </Grid>}
    </StyledTooltipPaper>
  )

  return (
    <>
      <StyledTravelLeg
        key={leg.id}
        $left={legLeftPosition}
        $width={legWidth}
        $color={leg.color || legDefaultColor}
        $strip={leg.isIdleLeg || false}
      >
        {leg.isLoadEnd && <StyledLoadIndicator />}
      </StyledTravelLeg>
      <CustomTooltip
        title={tooltipContent}
        placement="bottom-start"
        disableInteractive={false}
      >
        <StyledLegDropZone
          ref={dropRef}
          $left={legLeftPosition}
          $width={legWidth}
        >
          {isOver && <StyledDropZoneOverlay />}
        </StyledLegDropZone>
      </CustomTooltip>
    </>
  )
}

const StyledTravelLeg = styled('div',
  { shouldForwardProp: (prop) => prop !== '$left' && prop !== '$width' && prop !== '$color' && prop !== '$strip' },
) <{ $left: number; $width: number; $color: string; $strip: boolean }>`
  position: absolute;
  top: ${pxToRem(14)};
  left: ${props => pxToRem(props.$left)};
  width: ${props => pxToRem(props.$width)};
  height: ${pxToRem(8)};
  background-color: ${props => props.$color};
  background-image: ${props => props.$strip ?
    `linear-gradient(45deg, 
      rgba(255, 255, 255, 0.5) 25%, 
      transparent 25%, 
      transparent 50%, 
      rgba(255, 255, 255, 0.5) 50%, 
      rgba(255, 255, 255, 0.5) 75%, 
      transparent 75%, 
      transparent
    )` : 'none'};
  background-size: ${props => props.$strip ? `${pxToRem(8)} ${pxToRem(8)}` : 'auto'};
  opacity: 0.4;
  overflow: visible;
  z-index: 1;
`

const StyledLoadIndicator = styled('div')`
  position: absolute;
  top: -14px;
  width: 100%;
  height: 36px;
  min-width: 4px;
  background-color: ${props => props.theme.palette.success.main};
  opacity: 40%;
  z-index: -1;
`

const StyledLegDropZone = styled('div',
  { shouldForwardProp: (prop) => prop !== '$left' && prop !== '$width' },
) <{ $left: number; $width: number }>`
  position: absolute;
  top: ${pxToRem(2)};
  left: ${props => pxToRem(props.$left)};
  width: ${props => pxToRem(props.$width)};
  min-width: ${pxToRem(4)};
  height: ${pxToRem(32)};
  opacity: 0.4;
  z-index: 2;
`

const StyledDropZoneOverlay = styled('div')`
  width: 100%;
  height: 100%;
  border-radius: ${pxToRem(4)};
  background-color: ${props => props.theme.palette.secondary.main};
`